import { IKeyValue, setSettings, SettingsModel } from "@shared/library";
import { languages } from "@shared/library/dist/static/language";
import axios, { AxiosError } from "axios";
import React, { JSX, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import config from '../../../../config';
import importer from '../../../../helpers/importer';
import './Sidebar.scss';
//COMPONENTS
import Slide from '@mui/material/Slide';
//ICONS
import { WarningAmber } from "@mui/icons-material";
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
//MODELS
import { MenuArea_model } from "../../../../models/ui_data/MenuArea_model";
import { MenuOption_model } from "../../../../models/ui_data/MenuOption_model";
import { LangKey } from "../../../../models/ui_data/Lang_model";
//CONTEXTS
import { CntxConfig } from '../../../WebGest';

const Sidebar = (props: { isOpened: boolean; setIsOpened: React.Dispatch<React.SetStateAction<boolean>>; }) => {
    //CONTEXTS USE
    const cntxConfig = React.useContext(CntxConfig)!;

    //#region STATIC DATA
    const lang = languages(cntxConfig.settings?.lang);
    const navigate = useNavigate();
    const location = useLocation();
    const muiIcons: IKeyValue<JSX.Element> = {
        alarms_manager: <WarningAmber htmlColor="white" />
    };
    //#endregion

    //#region STATES
    const [menuWindow, setMenuWindow] = useState<any>({ isOpened: false, top: 0 });
    const [menu, setMenu] = React.useState<MenuArea_model[]>([]);
    const [selectedArea, setSelectedArea] = React.useState<MenuArea_model>();
    const [subMenu, setSubMenu] = React.useState<MenuOption_model[]>([]);
    const [windowSubMenu, setWindowSubMenu] = React.useState<any>({ label: '', subs: [] });
    const refSidebar = React.useRef<HTMLElement>(null);
    const refSidebarMenu = React.useRef<HTMLDivElement>(null);
    const topSideRef = React.useRef<HTMLDivElement>(null);
    const topMenuRef = React.useRef<HTMLDivElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    //FLAGS
    const [showScrollArrow, setShowScrollArrow] = React.useState<boolean>(false);
    //#endregion

    //#region METHODS
    //#region API CALLS
    const getMenuAPI = () =>
        axios.get<{ data: MenuArea_model[]; }>(`${config.api.internal}/Settings/menu`, { withCredentials: true })
            .then((res) => {
                const menu = res.data.data;

                localStorage.setItem('menu', JSON.stringify(menu));
                setMenu(menu);
            })
            .catch((err: AxiosError) => {
                console.error(err);
            });

    const getSettingsAPI = () =>
        axios.get<{ data: SettingsModel; }>(`${config.api.internal}/Settings`, { withCredentials: true })
            .then(res => {
                const settingsRES = res.data.data;
                localStorage.setItem('settings', JSON.stringify(settingsRES));
                setSettings(cntxConfig, settingsRES);
            })
            .catch((err: AxiosError) => {
                console.error(err);
            });
    //#endregion

    const initSidebar = <T extends MenuArea_model[] | SettingsModel | null>(init: "menu" | "settings", api: (() => void), set?: (c?: T) => void) => {
        const stored = localStorage.getItem(init);

        if (stored) {
            const parsed: T = JSON.parse(stored);
            set?.(parsed);
        }
        else {
            api();
        }
    };

    const printMenuItem = (area: MenuArea_model, isHomeButton: boolean = false) =>
        <div key={area.id} className={`item ${selectedArea?.id === area.id ? 'selected' : ''} ${isHomeButton ? ' home' : ''}`}
            onAuxClick={(e) => {
                if (area.subs.length === 1) {
                    const option = area.subs[0];
                    // Apri in una nuova scheda solo se è stato cliccato il tasto della rotellina
                    if (option.enabled && option.path && e.button === 1) window.open(option.path, '_blank');
                }
            }}
            onClick={() => {
                if (area.subs.length === 1) {
                    const option = area.subs[0];
                    if (option.enabled && option.path) {
                        setSelectedArea(area);
                        setSubMenu(area.subs);
                        props.setIsOpened(false);

                        navigate(option.path);
                    }
                }
                if (props.isOpened) {   // setta il submenu solo se aperto
                    setSubMenu(area.subs);
                    setSelectedArea(area);
                }
            }}
            onMouseEnter={event => {
                if (!props.isOpened) {  // popola il submenu della window
                    setWindowSubMenu(area);
                    openMenuWindow(event);
                }
            }}
            onMouseLeave={closeMenuWindow}
        >
            {muiIcons[area.label] ?? <img className="protect" src={importer.ic.require(area.icon)} alt="" />}
        </div>;

    const showArrow = () => {
        if (menuRef.current) {
            const showingArrow = menuRef.current.clientHeight < menuRef.current.scrollHeight &&
                (menuRef.current.scrollTop + menuRef.current.clientHeight) < menuRef.current.scrollHeight;
            setShowScrollArrow(showingArrow);
        }
    };

    const openMenuWindow = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const { y } = event.currentTarget.getBoundingClientRect();
        setMenuWindow({ isOpened: true, top: y });
    };
    const closeMenuWindow = () => setMenuWindow({ isOpened: false, top: 0 });

    const handleClickOutside = (event: any) => {
        if (!refSidebar.current?.contains(event.target) && !refSidebarMenu.current?.contains(event.target)) {
            // Clic al di fuori del div, eseguire l'azione desiderata qui
            props.setIsOpened(false);
        }
    };
    //#endregion

    //#region STATES CHANGE
    React.useEffect(() => {
        initSidebar("menu", getMenuAPI, (menu?: MenuArea_model[]) => setMenu(menu ?? []));
        initSidebar("settings", getSettingsAPI, (settings?: SettingsModel) => setSettings(cntxConfig, settings));

        const observer = new ResizeObserver(entries => {
            if (entries.find(e => e.target === menuRef.current)) {
                showArrow();
            }
        });

        if (menuRef.current) {   // Collega l'osservatore alla tabella
            observer.observe(menuRef.current);
            menuRef.current.addEventListener('scroll', showArrow);
        }

        return () => {
            if (menuRef.current) {
                observer.unobserve(menuRef.current);
                menuRef.current.removeEventListener('scroll', showArrow);
            }
        };
    }, []);

    React.useEffect(() => {
        if (props.isOpened) {
            // Aggiunge un event listener al documento per gestire i clic
            document.addEventListener('click', handleClickOutside);

            // Pulisce l'event listener quando il componente si smonta
            return () => {
                document.removeEventListener('click', handleClickOutside);
            };
        }
    }, [props.isOpened]);

    React.useEffect(() => {
        const selected = menu.find(a => a.subs.find(s => location.pathname === s.path));

        setSelectedArea(selected);
        setSubMenu(selected?.subs ?? []);
    }, [menu, props.isOpened]);
    //#endregion

    return <>
        <aside id="gestSidebar" ref={refSidebar}>
            {menuWindow.isOpened &&
                <div
                    id="menuWindow"
                    style={{ top: menuWindow.top }}
                    onMouseEnter={() => setMenuWindow({ isOpened: true, top: menuWindow.top })}
                    onMouseLeave={closeMenuWindow}
                >
                    <div className='window'>
                        <div className="pageTitle"><h4>{lang[windowSubMenu.label as LangKey]}</h4></div>

                        {windowSubMenu.subs.map((option: any, k: number) =>
                            <a key={k}
                                href={option.path}
                                className={`item ${option.enabled ? '' : 'disabled'} ${location.pathname === option.path ? 'selected' : ''}`}
                                onAuxClick={event => {
                                    // Apri in una nuova scheda solo se è stato cliccato il tasto della rotellina
                                    if (option.enabled && option.path && event.button === 1) {
                                        window.open(option.path, '_blank');
                                    };
                                }}
                                onClick={event => {
                                    event.preventDefault();

                                    if (option.enabled && option.path) {
                                        const area = menu[(menu.findIndex(v => v.id === option.areaID))];
                                        setSelectedArea(area);
                                        setSubMenu(area.subs);

                                        navigate(option.path);
                                    }
                                }}
                            >
                                {muiIcons[option.label] ?? <img className="protect" src={importer.ic.require(option.icon)} alt="" loading="lazy" />}
                                <h5>{lang[option.label as LangKey] ?? option.label}</h5>
                            </a>
                        )}
                    </div>
                </div>
            }

            <div ref={topSideRef} className='topSide'>
                <div ref={topMenuRef} className="topMenu">
                    <div
                        onClick={() => props.setIsOpened(!props.isOpened)}
                        className="item"
                    >
                        {<img src={importer.ic.require(`menu${props.isOpened ? "Close" : "Open"}.svg`)} alt="" loading="lazy" />}
                    </div>

                    {menu[0] && printMenuItem(menu[0], true)}
                </div>

                <div ref={menuRef} className="menu">
                    {menu.map((area, k) => !k ? null : printMenuItem(area))}
                </div>

                {showScrollArrow === true && <div className='scrollBelow'><ArrowDropDownRoundedIcon /></div>}
            </div>
        </aside>

        <Slide direction="right" in={props.isOpened}>
            <div
                ref={refSidebarMenu}
                id="menu"
                className={"menu " + (props.isOpened ? 'opened' : '')}
            >
                <div className="pageTitle"><h4>{lang[selectedArea?.label as LangKey] || selectedArea?.label}</h4></div>

                <div className='menuItems'>
                    <div className="items">
                        {subMenu.map((option, k) => <a key={k}
                            href={option.path}
                            className={`item ${option.enabled ? '' : 'disabled'} ${location.pathname === option.path ? 'selected' : ''}`}
                            onAuxClick={event => {
                                // Apri in una nuova scheda solo se è stato cliccato il tasto della rotellina
                                if (option.enabled && option.path && event.button === 1) {
                                    window.open(`${option.path}`, '_blank');
                                };
                            }}
                            onClick={event => {
                                event.preventDefault();

                                if (option.enabled && option.path) {
                                    props.setIsOpened(false);
                                    navigate(option.path);
                                }
                            }}
                        >
                            {muiIcons[option.label] ?? <img className="protect" src={importer.ic.require(option.icon)} alt="" />}
                            <h5>{lang[option.label as LangKey] ?? option.label}</h5>
                        </a>
                        )}
                    </div>
                </div>

                <div className='profile'>
                    <div>
                        <h4>{`${cntxConfig.session?.userData?.firstname} ${cntxConfig.session?.userData?.lastname}`}</h4>
                        <h5>@{cntxConfig.session?.userData?.username}</h5>
                    </div>
                </div>
            </div>
        </Slide></>;
};
export default Sidebar;